import { useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { WelcomeStep } from './steps/welcome-step';
import { PracticeStep } from './steps/practice-step';
import { RolesStep } from './steps/roles-step';
import { PractitionersStep } from './steps/practitioners-step';
import { StaffStep } from './steps/staff-step';
import { CompletionStep } from './steps/completion-step';

/**
 * Practice Setup onboarding wizard (PR 1 of 6 — shell + Welcome).
 *
 * Mirrors Flutter `practice_setup_module/practice_setup/presentation/pages/
 * practice_setup_screen.dart`. 6-tab structure, Flutter index order:
 *
 *   0 — Welcome (PracticeSetupStartWidget) — shipped here.
 *   1 — Add Practice Details (PR 2)
 *   2 — Review Roles           (PR 3) — NOTE: before Practitioners,
 *                                       per Flutter source, NOT the
 *                                       briefing's PR table.
 *   3 — Add Practitioners      (PR 4) — Flutter UI label, not "Doctors"
 *   4 — Add Staff              (PR 5)
 *   5 — Practice Created       (PR 6)
 *
 * Tabs 0 and 5 render their own controls (Welcome has Start; Completion
 * has Finish-and-go-home). Tabs 1-4 share the shell's Back / Next bar,
 * with "Next" relabelled "Finish" on tab 4 (the last data-entry step
 * before the completion tab). Per-tab `setNextEnabled(true)` gates the
 * shell's Next button — the placeholder steps shipped in this PR call
 * it unconditionally so the user can click through.
 *
 * Mount: `App.tsx` renders this when
 *   user.role === '1' (orgAdminRole per CLAUDE.md role IDs)
 *   AND !user.isOnboarded
 *
 * Per CLAUDE.md "Flutter-web workaround simplification rule": the
 * Flutter `TabController` + `AbsorbPointer`-wrapped `TabBar` is a
 * canvas-rendering accommodation. React replaces with plain `useState`
 * for the active index — tab clicks are intentionally NOT exposed
 * (matches Flutter's AbsorbPointer behaviour: navigation is via Back /
 * Next only, no jumping).
 */
export interface OnboardingWizardProps {
  /** First name from the JWT — used in the Welcome greeting. */
  firstName: string | null | undefined;
  /**
   * Fires after the user finishes the completion tab (PR 6). PR 1
   * surfaces the prop in the API shape; PR 6 will actually call it
   * with the auth-store + localStorage `isOnboarded = true` write.
   */
  onComplete: () => void;
}

interface StepDef {
  key: string;
  /** Short label for the indicator strip (e.g. "Add Practice Details"). */
  label: string;
  /** Shown above the label in the indicator (e.g. "STEP 1"). */
  stepNumber: string | null;
}

// Tab definitions — Flutter order, see file header.
// Welcome (0) and Completion (5) have stepNumber: null because they're
// hidden from the STEP 1..4 indicator strip in Flutter (tabs 0 and 5
// render as `Container(width: 0)` in the TabBar at lines 194, 221).
const STEPS: StepDef[] = [
  { key: 'welcome', label: 'Welcome', stepNumber: null },
  { key: 'practice', label: 'Add practice details', stepNumber: 'STEP 1' },
  { key: 'roles', label: 'Review roles', stepNumber: 'STEP 2' },
  { key: 'practitioners', label: 'Add practitioners', stepNumber: 'STEP 3' },
  { key: 'staff', label: 'Add staff', stepNumber: 'STEP 4' },
  { key: 'completion', label: 'Practice created', stepNumber: null },
];

const VISIBLE_INDICATOR_STEPS = STEPS.filter((s) => s.stepNumber !== null);

export function OnboardingWizard({
  firstName,
  onComplete,
}: OnboardingWizardProps) {
  const [tabIndex, setTabIndex] = useState(0);
  const [nextEnabled, setNextEnabled] = useState(false);

  const goNext = () => {
    if (tabIndex < STEPS.length - 1) {
      setTabIndex(tabIndex + 1);
      setNextEnabled(false);
    }
  };
  const goBack = () => {
    if (tabIndex > 1) {
      setTabIndex(tabIndex - 1);
      setNextEnabled(false);
    }
  };

  // Flutter parity (`practice_setup_screen.dart:112-157`):
  //   - Back / Next bar is hidden on tabs 0 (Welcome) and 5 (Completion).
  //   - Back is disabled on tab 1 (`onPressed: (selectedTabIndex == 1) ? null : ...`).
  //   - Next button label changes to "Finish" on tab 4.
  const showShellControls = tabIndex > 0 && tabIndex < 5;
  const backDisabled = tabIndex === 1;
  const nextLabel = tabIndex === 4 ? 'Finish' : 'Next';

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[var(--color-surface-background)]">
      <header className="flex h-[168px] flex-col bg-[var(--surface-read)] shadow-[0_1px_0_var(--surface-line)]">
        <div className="flex items-center justify-between px-8 pt-8">
          {showShellControls ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={backDisabled}
              onClick={goBack}
            >
              Back
            </Button>
          ) : (
            <div className="h-8" aria-hidden />
          )}
          {showShellControls ? (
            <Button
              type="button"
              size="sm"
              disabled={!nextEnabled}
              onClick={goNext}
            >
              {nextLabel}
            </Button>
          ) : (
            <div className="h-8" aria-hidden />
          )}
        </div>

        <StepIndicator activeIndex={tabIndex} />
      </header>

      <main className="flex-1 overflow-auto scrollbar-hide">
        <StepBody
          stepKey={STEPS[tabIndex]?.key ?? 'welcome'}
          firstName={firstName}
          onStart={goNext}
          onCompletionFinish={onComplete}
          enableNext={() => setNextEnabled(true)}
        />
      </main>
    </div>
  );
}

/**
 * STEP 1..4 indicator strip. Box-outlined for the active step, bare
 * label otherwise. Tabs 0 (Welcome) and 5 (Completion) suppress the
 * surrounding box per Flutter `practice_setup_screen.dart:186-191`.
 *
 * Selected style: 2px brand-primary border + rounded-7. Past steps
 * keep their default unselected styling (the Flutter `isSuccess` prop
 * paints them grey-coloured — mirrored here as text-text-muted).
 */
function StepIndicator({ activeIndex }: { activeIndex: number }) {
  return (
    <div className="flex flex-1 items-center px-8 pb-6">
      <div className="flex gap-2">
        {VISIBLE_INDICATOR_STEPS.map((step, i) => {
          // `i` is 0-based into VISIBLE_INDICATOR_STEPS (4 items);
          // map to absolute tab index by adding 1 (skipping Welcome at 0).
          const absoluteIndex = i + 1;
          const isActive = absoluteIndex === activeIndex;
          const isPast = absoluteIndex < activeIndex;
          return (
            <div
              key={step.key}
              className={cn(
                'flex items-center gap-2 rounded-[8px] px-3 py-2',
                isActive
                  ? 'border-2 border-[var(--color-blue-500)]'
                  : 'border-2 border-transparent',
              )}
            >
              <div
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full',
                  isActive
                    ? 'bg-[var(--color-blue-500)] text-white'
                    : isPast
                      ? 'bg-[var(--color-status-success)] text-white'
                      : 'bg-[var(--color-grey-100)] text-[var(--ink-meta)]',
                )}
              >
                {isPast ? (
                  <Check className="h-4 w-4" aria-hidden />
                ) : (
                  <span className="text-xs font-semibold">{absoluteIndex}</span>
                )}
              </div>
              <div className="flex flex-col leading-tight">
                <span
                  className={cn(
                    'text-xs font-medium',
                    isActive
                      ? 'text-[var(--accent-primary)]'
                      : 'text-[var(--ink-meta)]',
                  )}
                >
                  {step.stepNumber}
                </span>
                <span
                  className={cn(
                    'text-sm font-semibold',
                    isActive
                      ? 'text-[var(--ink-body)]'
                      : 'text-[var(--ink-meta)]',
                  )}
                >
                  {step.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepBody({
  stepKey,
  firstName,
  onStart,
  onCompletionFinish,
  enableNext,
}: {
  stepKey: string;
  firstName: string | null | undefined;
  onStart: () => void;
  onCompletionFinish: () => void;
  enableNext: () => void;
}): ReactNode {
  switch (stepKey) {
    case 'welcome':
      return <WelcomeStep firstName={firstName} onStart={onStart} />;
    case 'practice':
      return <PracticeStep enableNext={enableNext} />;
    case 'roles':
      return <RolesStep enableNext={enableNext} />;
    case 'practitioners':
      return <PractitionersStep enableNext={enableNext} />;
    case 'staff':
      return <StaffStep enableNext={enableNext} />;
    case 'completion':
      return <CompletionStep onFinish={onCompletionFinish} />;
    default:
      return null;
  }
}
